import dedent from 'ts-dedent'
import {
  ColumnDataType,
  Language,
  Provider,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentCodeParams,
  WorkflowComponentMetadata,
  WorkflowComponentParamType
} from '../../types'
import { createTableWithBBox } from '../../utils/databricks'
import { DBricksConnection } from '../../workflow/connections/databricks'
import { splitPairs } from './enrichcomponent'
import {
  SINGLE_OUTPUT_NAME,
  SingleOutputWorkflowComponent
} from './singleoutputcomponent'

class EnrichComponentDatabricks extends SingleOutputWorkflowComponent {
  colTypeName: string
  toGeomFunction: (column: string) => string
  isGeomWkb: boolean
  constructor(
    metadata: WorkflowComponentMetadata,
    colTypeName: string,
    colTypeTypes: ColumnDataType[],
    toGeomFunction: (column: string) => string,
    isGeomWkb: boolean
  ) {
    super(metadata, [
      {
        name: 'target',
        type: WorkflowComponentParamType.InputTable,
        title: 'Target table to be enriched',
        placeholder: 'FQN of the target table'
      },
      {
        name: 'source',
        type: WorkflowComponentParamType.InputTable,
        title: 'Source table',
        description: 'Source for the enrichment',
        placeholder: 'FQN of the source table'
      },
      {
        name: 'target_col',
        type: WorkflowComponentParamType.Column,
        title: `Target ${colTypeName} column`,
        parent: 'target',
        dataType: colTypeTypes
      },
      {
        name: 'source_col',
        type: WorkflowComponentParamType.Column,
        title: `Source geo column`,
        parent: 'source',
        dataType: [ColumnDataType.Geography, ColumnDataType.Geometry],
        optional: false
      },
      {
        name: 'variables',
        type: WorkflowComponentParamType.SelectColumnAggregation,
        title: 'Variables',
        parent: 'source',
        noDefault: true,
        doVariables: true,
        description:
          'Comma-separated list of variable names and aggregation methods (e.g. population_93405ad7, sum, population_93405ad7, avg)',
        placeholder: 'population_93405ad7, sum, population_93405ad7, avg',
        optional: false
      }
    ])
    this.colTypeName = colTypeName
    this.toGeomFunction = toGeomFunction
    this.isGeomWkb = isGeomWkb
  }

  supportedProviders(): Provider[] {
    return [Provider.DatabricksRest]
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const dryRun = codeType === WorkflowCodeType.DryRun
    const outputTablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    const preparedParams = this._prepareParameters(params)
    const variables = preparedParams.variables as string
    const variablePairs = splitPairs(
      variables?.split(',')?.map((x) => x?.trim())
    )
    let query = ''
    if (dryRun) {
      const selectQuery = dedent`
        SELECT
          *,
          ${variablePairs
            .map(([variable, aggregation]) => `0 AS ${variable}_${aggregation}`)
            .join(',\n')}
        FROM
          ${connection.quoteName(preparedParams.target as string)}`
      query = connection.getCreateTempTableQuery(
        outputTablename,
        selectQuery,
        codeType,
        null
      )
      query = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(query)
    } else {
      const processedSourceTablename = connection.quoteName(
        outputTablename + '_sourceprocessed'
      )
      const createSourceTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        preparedParams.source as string,
        processedSourceTablename,
        preparedParams.source_col as string,
        (x) => `ST_GEOMFROMWKB(${x})`
      )
      const processedTargetTablename = connection.quoteName(
        outputTablename + '_targetprocessed'
      )
      const createTargetTableQuery = createTableWithBBox(
        connection as DBricksConnection,
        preparedParams.target as string,
        processedTargetTablename,
        preparedParams.target_col as string,
        this.isGeomWkb
          ? (x) => `ST_GEOMFROMWKB(${this.toGeomFunction(x)})`
          : this.toGeomFunction
      )

      const select = this._getEnrichedPolygonsQuery(
        processedSourceTablename,
        preparedParams.source_col as string,
        processedTargetTablename,
        preparedParams.target_col as string,
        preparedParams.target as string,
        variablePairs,
        connection as DBricksConnection
      )

      const createOutputTableQuery = connection.getCreateTempTableQuery(
        outputTablename,
        select,
        codeType,
        null
      )
      const createGetDimensionFunction = this._getCreateGetDimensionFunction()

      query = dedent`
        ${createGetDimensionFunction}
        ${createSourceTableQuery}
        ${createTargetTableQuery}
        ${(connection as DBricksConnection).wrapSQLCodeAsPythonCall(
          createOutputTableQuery
        )}`
    }

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  protected _getEnrichedPolygonsQuery(
    processedSourceTablename: string,
    source_col: string,
    processedTargetTablename: string,
    target_col: string,
    target: string,
    variablePairs: string[][],
    connection: DBricksConnection
  ): string {
    return dedent`
        WITH
        input_geoms AS (
          SELECT
            ${this.toGeomFunction(`ANY_VALUE(${target_col})`)} AS geom,
            ANY_VALUE(${target_col}) AS geom_org,
            ANY_VALUE(xmin) AS xmin,
            ANY_VALUE(xmax) AS xmax,
            ANY_VALUE(ymin) AS ymin,
            ANY_VALUE(ymax) AS ymax
          FROM
            ${connection.quoteName(processedTargetTablename)}
          GROUP BY ${target_col}
        ),
        enrich_data AS (
          SELECT
            ${
              this.isGeomWkb
                ? `${source_col} AS geom,`
                : `ST_GEOMFROMWKB(${source_col}) AS geom,`
            }
            ${source_col} AS geom_org,
            ${variablePairs
              .map(([variable, aggregation]) => `${variable}`)
              .join(',\n')},
            xmin,
            xmax,
            ymin,
            ymax
          FROM
            ${connection.quoteName(processedSourceTablename)}
        ),
        intersections AS (
          SELECT
            CASE st_dimension_udf(enrich_data.geom_org)
              WHEN 0 THEN
                1
              WHEN 1 THEN
                ST_LENGTH(ST_INTERSECTION(enrich_data.geom, input_geoms.geom))
              WHEN 2 THEN
                ST_AREA(ST_INTERSECTION(enrich_data.geom, input_geoms.geom))
            END
            AS __carto_intersection,
            CASE st_dimension_udf(enrich_data.geom_org)
              WHEN 0 THEN
                1
              WHEN 1 THEN
                ST_LENGTH(enrich_data.geom)
              WHEN 2 THEN
                ST_AREA(enrich_data.geom)
            END
            AS __carto_total,
            input_geoms.geom,
            input_geoms.geom_org,
            ${variablePairs
              .map(([variable, aggregation]) => `${variable}`)
              .join(',\n')}
          FROM
            input_geoms,
            enrich_data
          WHERE
            (
              input_geoms.xmax >= enrich_data.xmin
              AND input_geoms.xmin <= enrich_data.xmax
              AND input_geoms.ymax >= enrich_data.ymin
              AND input_geoms.ymin <= enrich_data.ymax
            )
          AND
            ST_INTERSECTS(input_geoms.geom, enrich_data.geom)
        ),
        aggregation AS (
          SELECT
            geom_org,
            ANY_VALUE(geom) AS geom,
            ${variablePairs.map(([variable, aggregation]) => {
              switch (aggregation.toLowerCase()) {
                case 'sum':
                  return `SUM(${variable} * __carto_intersection / NULLIF(__carto_total, 0)) AS ${variable}_sum`
                case 'avg':
                  return `SUM(${variable} * __carto_intersection) / NULLIF(SUM(IF(${variable} IS NOT NULL,__carto_intersection,NULL)),0) AS ${variable}_avg`
                case 'min':
                  return `MIN(${variable}) AS ${variable}_min`
                case 'max':
                  return `MAX(${variable}) AS ${variable}_max`
                case 'count':
                  return `COUNT(${variable}) AS ${variable}_count`
                default:
                  return ''
              }
            })}
          FROM intersections
          GROUP BY geom_org
        )
        SELECT /*+ REPARTITION(40, ${source_col}) */
          target.*,
          ${variablePairs
            .map(
              ([variable, aggregation]) =>
                `aggregation.${variable}_${aggregation}`
            )
            .join(',\n')}
        FROM
          ${connection.quoteName(target as string)} AS target
        JOIN
          aggregation
        ON
          target.${target_col} = aggregation.geom_org
        `
  }
  protected _getCreateGetDimensionFunction(): string {
    return dedent`
        import struct

        def st_dimension(wkb_geom):
            endianness = wkb_geom[0]
            if endianness == 1:
                geometry_type = struct.unpack('<I', wkb_geom[1:5])[0]
            elif endianness == 0:
                geometry_type = struct.unpack('>I', wkb_geom[1:5])[0]
            else:
                raise ValueError(f"Invalid endianness byte: {endianness}")

            geometry_to_dimension = {
                1: 0,  # Point
                2: 1,  # LineString
                3: 2,  # Polygon
                4: 0,  # MultiPoint
                5: 1,  # MultiLineString
                6: 2   # MultiPolygon
            }

            if geometry_type in geometry_to_dimension:
                return geometry_to_dimension[geometry_type]
            else:
                raise ValueError(f"Unsupported geometry type: {geometry_type}")

        from pyspark.sql.functions import udf
        from pyspark.sql.types import IntegerType

        st_dimension_udf = udf(st_dimension, IntegerType())

        spark.udf.register("st_dimension_udf", st_dimension_udf)
        `
  }

  language(provider: Provider): Language {
    return Language.Python
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    const classifiedMethods: Record<string, Array<string>> = {}
    classifiedMethods[ColumnDataType.Number] = [
      'min',
      'max',
      'avg',
      'sum',
      'count'
    ]
    return {
      methods: classifiedMethods,
      allowedTypes: [ColumnDataType.Number],
      excludedNames: ['geoid', 'geom']
    }
  }
}

export { EnrichComponentDatabricks }
